import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { Box, Chip, Icon, Tooltip, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_PRI_MED, CF_SEC_MED } from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import type { RootState } from '../../../shared/types';
import {
  formatDisply,
  formatMulti as formatMultiShared,
} from '../../../shared/utils';
import type { AppliedColumnFilters } from '../AuditLogColumnFilter';
import { isColumnVisible } from '../AuditLogColumnFilter';
import {
  useLazyGetAuditLogReviewsQuery,
  usePostAuditLogReviewMutation,
} from '../data-layer/auditLogApi';
import type { ReviewActivity, ReviewStatus } from '../data-layer/auditLogTypes';
import { auditLogActions } from '../data-store/auditLogSlice';
import { useApiCancellation, useDateFormat } from '../hooks';
import { dispatchActivityLogMutationFailureBanner } from '../utils/dispatchActivityLogMutationFailureBanner';
import { formatChangeIndicatorLabel, formatDateByGlobal } from '../utils/utils';

import TimelineItemActions from './TimelineItemActions';
import type { FlagData, TimelineData } from './types';

import './TimelineItem.css';

export interface TimelineItemProps {
  item: TimelineData;
  isSelected?: boolean;
  isLast?: boolean;
  onClick?: ((tab?: 'summary' | 'review-activity') => void) | undefined;
  /** Click handler for the timestamp text only (does not trigger row click). */
  onTimestampClick?: (() => void) | undefined;
  className?: string;
  appliedColumnFilters?: AppliedColumnFilters;
  visibleBaseKeys?: string[];
  isDetailPanelOpen?: boolean;
}

/**
 * TimelineItem Component
 *
 * Individual timeline event item that displays event details exactly as shown in Figma design.
 * Features vertical timeline with timestamp, event title, and attribute details.
 *
 * Memoized to prevent unnecessary re-renders when props haven't changed.
 */
function TimelineItemComponent({
  item,
  isSelected = false,
  isLast = false,
  onClick,
  onTimestampClick = undefined,
  className = '',
  appliedColumnFilters = undefined,
  visibleBaseKeys = undefined,
  isDetailPanelOpen = false,
}: TimelineItemProps): React.ReactElement {
  const l = useInvertedLocalize();
  const globalDateFormat = useDateFormat();
  const PLUS = '+';
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const [postReview] = usePostAuditLogReviewMutation();
  const [fetchReviews] = useLazyGetAuditLogReviewsQuery();
  const { registerAbort, cancelRequest } = useApiCancellation();
  const cookies = new Cookies();
  const userKey = String(cookies.get('userKey') ?? '');

  // Get requestMetadata from Redux
  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );

  // All review activities in store (used as fallback for latestActivity)
  const reviewActivitiesByLogId = useTypedSelector(
    (s) => s.aag.auditLog.reviewActivitiesByLogId
  );

  // Readonly mode flag (driven by /requestReviewDetails)
  const isReadOnly = Boolean(
    useTypedSelector((s) => s.aag.auditLog.isReadOnly)
  );

  // Flag popover state
  const [isFlagPopoverOpen, setIsFlagPopoverOpen] = useState(false);

  // Close popover on scroll
  useEffect(() => {
    const handleClosePopovers = () => {
      setIsFlagPopoverOpen(false);
    };

    window.addEventListener('closePopovers', handleClosePopovers);

    return () => {
      window.removeEventListener('closePopovers', handleClosePopovers);
    };
  }, []);
  // Local override to reflect immediate UI status change after resolve
  const [overrideRowStatus, setOverrideRowStatus] = useState<string | null>(
    null
  );

  // Clear override when Redux status changes from elsewhere (e.g. resolve from detail panel)
  React.useEffect(() => {
    if (overrideRowStatus !== null && item.rowLogStatus !== overrideRowStatus) {
      setOverrideRowStatus(null);
    }
  }, [item.rowLogStatus, overrideRowStatus]);

  const attributeRowRef = useRef<HTMLDivElement | null>(null);
  const [attributeCapacity, setAttributeCapacity] = React.useState<
    number | null
  >(null);

  // Observe container width and compute how many 20rem columns fit with 2.25rem gaps
  React.useEffect(() => {
    const GAP_REM = 2.25;
    const COL_REM = 20;
    const remInPx =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

    const container = attributeRowRef.current
      ?.parentElement as HTMLElement | null;

    if (!container) {
      return () => {};
    }

    const computeAndSet = () => {
      const widthPx = container.clientWidth;
      const colPx = COL_REM * remInPx;
      const gapPx = GAP_REM * remInPx;
      const cap =
        widthPx > 0
          ? Math.max(1, Math.floor((widthPx + gapPx) / (colPx + gapPx)))
          : null;

      setAttributeCapacity(cap);
    };

    computeAndSet();

    let ro: ResizeObserver | null = null;

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      ro = new window.ResizeObserver(() => {
        computeAndSet();
      });
      ro.observe(container);
    }

    const onResize = (): void => {
      computeAndSet();
    };

    window.addEventListener('resize', onResize);

    return () => {
      if (ro) {
        ro.disconnect();
      }

      window.removeEventListener('resize', onResize);
    };
  }, []);

  // No GET /reviews subscription; badge uses search API value and POST responses

  // Strongly typed alias to avoid no-unsafe-call for action creator
  const setLatestActivityAction =
    auditLogActions.setTimelineItemLatestActivity as unknown as (payload: {
      uniqueLogId: string | number;
      activities: ReviewActivity[];
    }) => AnyAction;

  // Compute commented count from activities (TS-safe)
  const getCommentedCount = (activities: unknown): number => {
    if (!Array.isArray(activities)) {
      return 0;
    }

    return activities.filter((a) => {
      const status =
        a && typeof (a as { status?: unknown }).status === 'string'
          ? String((a as { status?: unknown }).status)
          : '';

      return status.toUpperCase() === 'COMMENTED';
    }).length;
  };

  const refreshCommentCountFromServer = async (): Promise<void> => {
    try {
      // Cancel previous reviews request if any
      cancelRequest('getAuditLogReviews');

      const promise = fetchReviews({
        uniqueLogId: item.uniqueLogId,
        requestAccessKey: requestMetadata?.requestAccessKey ?? '',
      });

      // Register abort function for this request
      const abortablePromise = registerAbort(
        promise as unknown as ReturnType<typeof fetchReviews> & {
          abort?: () => void;
        },
        'getAuditLogReviews'
      );

      const data = await abortablePromise.unwrap();

      const activitiesUnknown = (data as { reviewActivities: unknown })
        .reviewActivities;
      const commented = getCommentedCount(activitiesUnknown);

      dispatch(
        auditLogActions.updateTimelineItemCommentCount({
          uniqueLogId: item.uniqueLogId,
          commentsCount: commented,
        })
      );

      // Also sync latest activity and activities list from server
      const activities = Array.isArray(activitiesUnknown)
        ? (activitiesUnknown as ReviewActivity[])
        : [];

      dispatch(
        auditLogActions.setReviewActivities({
          uniqueLogId: item.uniqueLogId,
          activities,
        })
      );

      dispatch(
        setLatestActivityAction({
          uniqueLogId: item.uniqueLogId,
          activities,
        })
      );
    } catch {
      // best-effort sync; keep optimistic count from POST
    }
  };

  const handleFlagSave = async (comment: string): Promise<void> => {
    dispatch(auditLogActions.setIsPostingReview(true));

    const status: ReviewStatus = 'FLAGGED';

    try {
      const res = (await postReview({
        uniqueLogId: item.uniqueLogId,
        requestAccessKey: requestMetadata?.requestAccessKey ?? '',
        body: { comment, status, actionedBy: userKey },
      }).unwrap()) as { reviewActivities: ReviewActivity[] };

      // Close popover immediately to avoid flash
      setIsFlagPopoverOpen(false);

      dispatch(
        auditLogActions.setReviewActivities({
          uniqueLogId: item.uniqueLogId,
          activities: res.reviewActivities,
        })
      );
      // Update latest activity in the list so Summary tab reflects newest
      dispatch(
        setLatestActivityAction({
          uniqueLogId: item.uniqueLogId,
          activities: res.reviewActivities,
        })
      );
      // Reflect pending review immediately on UI and store
      dispatch(
        auditLogActions.setTimelineItemStatus({
          uniqueLogId: item.uniqueLogId,
          rowLogStatus: 'FLAGGED',
        })
      );
      setOverrideRowStatus('FLAGGED');

      // Update comment count from POST response; then reconcile with server
      const commentedCount1 = getCommentedCount(res.reviewActivities);

      dispatch(
        auditLogActions.updateTimelineItemCommentCount({
          uniqueLogId: item.uniqueLogId,
          commentsCount: commentedCount1,
        })
      );

      await refreshCommentCountFromServer();

      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(auditLogActions.setPostReviewError(null));
    } catch (e) {
      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(
        auditLogActions.setPostReviewError(
          e instanceof Error ? e.message : l('Failed to post review')
        )
      );
      dispatchActivityLogMutationFailureBanner(dispatch, e, l);
    }
  };

  const handleCommentSave = async (comment: string): Promise<void> => {
    dispatch(auditLogActions.setIsPostingReview(true));

    const status: ReviewStatus = 'COMMENTED';

    try {
      const res = (await postReview({
        uniqueLogId: item.uniqueLogId,
        requestAccessKey: requestMetadata?.requestAccessKey ?? '',
        body: { comment, status, actionedBy: userKey },
      }).unwrap()) as { reviewActivities: ReviewActivity[] };

      // Close popover immediately to avoid flicker
      setIsFlagPopoverOpen(false);

      dispatch(
        auditLogActions.setReviewActivities({
          uniqueLogId: item.uniqueLogId,
          activities: res.reviewActivities,
        })
      );
      dispatch(
        setLatestActivityAction({
          uniqueLogId: item.uniqueLogId,
          activities: res.reviewActivities,
        })
      );

      const commentedCount2 = getCommentedCount(res.reviewActivities);

      dispatch(
        auditLogActions.updateTimelineItemCommentCount({
          uniqueLogId: item.uniqueLogId,
          commentsCount: commentedCount2,
        })
      );

      // Preserve FLAGGED and RESOLVED - only set COMMENTED when item had no prior status
      const priorStatus = overrideRowStatus ?? item.rowLogStatus;
      const shouldKeepPriorStatus =
        priorStatus === 'FLAGGED' || priorStatus === 'RESOLVED';

      if (!shouldKeepPriorStatus) {
        dispatch(
          auditLogActions.setTimelineItemStatus({
            uniqueLogId: item.uniqueLogId,
            rowLogStatus: 'COMMENTED',
          })
        );
        setOverrideRowStatus('COMMENTED');
      }

      await refreshCommentCountFromServer();

      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(auditLogActions.setPostReviewError(null));
    } catch (e) {
      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(
        auditLogActions.setPostReviewError(
          e instanceof Error ? e.message : l('Failed to post review')
        )
      );
      dispatchActivityLogMutationFailureBanner(dispatch, e, l);
    }
  };

  const handleFlagResolve = async (comment: string): Promise<void> => {
    dispatch(auditLogActions.setIsPostingReview(true));

    try {
      const res = (await postReview({
        uniqueLogId: item.uniqueLogId,
        requestAccessKey: requestMetadata?.requestAccessKey ?? '',
        body: {
          comment,
          status: 'RESOLVED',
          actionedBy: userKey,
        },
      }).unwrap()) as { reviewActivities: ReviewActivity[] };

      // Close popover immediately to avoid any flash after status transitions
      setIsFlagPopoverOpen(false);

      dispatch(
        auditLogActions.setReviewActivities({
          uniqueLogId: item.uniqueLogId,
          activities: res.reviewActivities,
        })
      );
      dispatch(
        setLatestActivityAction({
          uniqueLogId: item.uniqueLogId,
          activities: res.reviewActivities,
        })
      );
      dispatch(
        auditLogActions.setTimelineItemStatus({
          uniqueLogId: item.uniqueLogId,
          rowLogStatus: 'RESOLVED',
        })
      );
      setOverrideRowStatus('RESOLVED');

      const commentedCount3 = getCommentedCount(res.reviewActivities);

      dispatch(
        auditLogActions.updateTimelineItemCommentCount({
          uniqueLogId: item.uniqueLogId,
          commentsCount: commentedCount3,
        })
      );

      await refreshCommentCountFromServer();

      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(auditLogActions.setPostReviewError(null));
    } catch (e) {
      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(
        auditLogActions.setPostReviewError(
          e instanceof Error ? e.message : 'Failed to resolve flag'
        )
      );
      dispatchActivityLogMutationFailureBanner(dispatch, e, l);
    }
  };

  const handleCommentClick = () => {
    setIsFlagPopoverOpen(true);
  };

  const handleResolveFlagClick = () => {
    // Open the popover only when the item is currently flagged
    setIsFlagPopoverOpen((open) => {
      const shouldOpen = (overrideRowStatus ?? item.rowLogStatus) === 'FLAGGED';

      return shouldOpen ? true : open;
    });
  };

  // Helper: normalize severity label from API (LOW | MEDIUM | HIGH | CRITICAL)
  const computedSeverity = useMemo(() => {
    const sev = item.severity.toUpperCase();

    if (sev === 'CRITICAL' || sev === 'HIGH') {
      return 'High';
    }

    if (sev === 'MEDIUM') {
      return 'Medium';
    }

    if (sev === 'LOW') {
      return 'Low';
    }

    return '-';
  }, [item.severity]);

  // Helper function to check if a column should be visible
  const shouldShowColumn = (columnKey: string): boolean => {
    if (!appliedColumnFilters) {
      return true; // Show all columns if no filters applied
    }

    return isColumnVisible(columnKey, appliedColumnFilters);
  };

  // Memoize severity chip styles to avoid recalculation on every render
  const severityChipStyle = useMemo(() => {
    switch (computedSeverity) {
      case 'Low':
        return {
          backgroundColor: 'var(--color-background-success-subtle, #e5f7e0)',
          color: 'var(--color-foreground-success-bold, #033a0f)',
        } as React.CSSProperties;
      case 'Medium':
        return {
          backgroundColor: 'var(--color-background-warning-subtle, #fff1d9)',
          color: 'var(--color-foreground-warning-bold, #5c3c00)',
        } as React.CSSProperties;
      case 'High':
        return {
          backgroundColor: 'var(--color-background-danger-subtle, #fdeeed)',
          color: 'var(--color-foreground-danger-bold, #700600)',
        } as React.CSSProperties;
      default:
        return undefined;
    }
  }, [computedSeverity]);

  // Memoize formatted timestamp to avoid recalculation on every render
  const formattedTimestamp = useMemo(() => {
    const ts = item.logCreatedAt;

    if (!ts) {
      return null;
    }

    return formatDateByGlobal(ts, globalDateFormat);
  }, [item.logCreatedAt, globalDateFormat]);

  const handleTimestampClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onTimestampClick) {
      return;
    }

    // Prevent triggering the timeline row click (detail panel) when clicking timestamp.
    e.preventDefault();
    e.stopPropagation();
    onTimestampClick();
  };

  // Map item.logType to group prefix used by the column picker
  const groupPrefix = useMemo(() => {
    const type = String(item.logType || '')
      .toLowerCase()
      .trim();

    // Use exact matching to avoid cross-contamination
    switch (type) {
      case 'security audit log':
        return 'SEC_AUDIT_LOG';
      case 'change log':
        return 'CHANGE_LOG';
      case 't-code log':
      case 'tcode log':
        return 'TCODE_LOG';
      case 'system log':
        return 'SYSTEM_LOG';
      default:
        return 'SEC_AUDIT_LOG'; // Default fallback
    }
  }, [item.logType]);

  const triggerAttributeRowClick = () => {
    if (onClick) {
      onClick('review-activity');
    } else if (attributeRowRef.current) {
      attributeRowRef.current.click();
    }
  };

  // Map rowLogStatus (consider local override) to flag button status
  const effectiveRowStatus = overrideRowStatus ?? item.rowLogStatus;
  let mappedFlagStatus: FlagData['status'] = 'none';

  if (effectiveRowStatus === 'RESOLVED') {
    mappedFlagStatus = 'resolved';
  } else if (effectiveRowStatus === 'FLAGGED') {
    mappedFlagStatus = 'needs_resolution';
  }

  // Build status chip for readOnly timeline item header
  const statusChip = useMemo(() => {
    if (!isReadOnly) {
      return null;
    }

    if (effectiveRowStatus === 'RESOLVED') {
      return (
        <Chip
          kind='boldFilled'
          size='small'
          color='success'
          leftIcon={<Icon kind='FlagResolved' size='xSmall' />}
          label={l('Resolved')}
        />
      );
    }

    if (effectiveRowStatus === 'COMMENTED') {
      return (
        <Chip
          kind='boldFilled'
          size='small'
          color='info'
          leftIcon={<Icon kind='Comment' size='xSmall' />}
          label={l('Commented ({count})', {
            count: item.commentsCount ?? 0,
          })}
        />
      );
    }

    return null;
  }, [isReadOnly, effectiveRowStatus, item.commentsCount, l]);

  // Build flag data for popovers using latestActivity when available
  const popoverFlagData: FlagData = useMemo(() => {
    // Ensure status reflects immediate resolved state
    const status: FlagData['status'] = mappedFlagStatus;

    // Try latestActivity from the item first
    const nameFromItem = item.latestActivity?.updatedBy;
    const commentFromItem = item.latestActivity?.comment;

    // Fallback: compute latest from Redux reviewActivities if item has no data
    const activities = reviewActivitiesByLogId[item.uniqueLogId] ?? [];

    const toTs = (v: unknown): number => {
      if (typeof v === 'number') {
        return v;
      }

      if (v instanceof Date) {
        return v.getTime();
      }

      if (typeof v === 'string') {
        const t = Date.parse(v);

        return Number.isFinite(t) ? t : 0;
      }

      return 0;
    };

    const latestFromStore =
      Array.isArray(activities) && activities.length > 0
        ? activities.reduce(
            (acc, curr) =>
              toTs((curr as { updatedAt?: unknown }).updatedAt) >=
              toTs((acc as { updatedAt?: unknown }).updatedAt)
                ? curr
                : acc,
            activities[0]
          )
        : undefined;

    const name = nameFromItem ?? latestFromStore?.user ?? undefined;
    const cmt = commentFromItem ?? latestFromStore?.comment ?? undefined;

    return {
      status,
      flaggedByName: name,
      flaggedBy: name,
      comment: cmt,
    };
  }, [
    mappedFlagStatus,
    item.latestActivity?.updatedBy,
    item.latestActivity?.comment,
    item.uniqueLogId,
    reviewActivitiesByLogId,
  ]);

  // Check visibility using Columns API ids as source of truth
  const shouldShowForApiId = (apiId: string): boolean => {
    // If we have explicit keys from the column picker, use them directly
    if (Array.isArray(visibleBaseKeys)) {
      return visibleBaseKeys.includes(apiId);
    }

    // If no filters applied, show all
    if (!appliedColumnFilters) {
      return true;
    }

    // Columns API uses `${groupPrefix}-${apiId}` keys
    return shouldShowColumn(`${groupPrefix}-${apiId}`);
  };

  // Render one attribute by Columns API id (source of truth)
  const renderAttributeByApiId = (apiId: string): React.ReactElement | null => {
    switch (apiId) {
      case 'tcodeValue':
      case 'entitlementDisplayName':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Entitlement')}
            </Typography>
            <Box className='Aag-TimelineItem-chip Aag-TimelineItem-chip--neutral'>
              <Typography kind='body3'>
                {formatDisply(item.entitlementDisplayName) ?? '-'}
              </Typography>
            </Box>
          </Box>
        );
      case 'severity':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Severity')}
            </Typography>
            {computedSeverity === '-' ? (
              <Typography
                lineLimit={3}
                kind='body2'
                className='Aag-TimelineItem-attributeValue'>
                {computedSeverity}
              </Typography>
            ) : (
              <Box
                className='Aag-TimelineItem-chip Aag-TimelineItem-chip--severity'
                style={severityChipStyle}>
                <Typography lineLimit={3} kind='body3'>
                  {computedSeverity}
                </Typography>
              </Box>
            )}
          </Box>
        );
      case 'auditClass':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Audit Class')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatDisply(item.auditClass, item.details, item.shortText) ??
                '-'}
            </Typography>
          </Box>
        );
      case 'tcodeDescription':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Entitlement Description')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatDisply(
                item.entitlementDescription,
                item.details,
                item.shortText
              ) ?? '-'}
            </Typography>
          </Box>
        );
      case 'clientName':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Client')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatDisply(item.client) ?? '-'}
            </Typography>
          </Box>
        );
      case 'timeZone':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Time Zone')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatDisply(item.timeZone) ?? '-'}
            </Typography>
          </Box>
        );
      case 'tableKey':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Log ID')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatDisply(item.tableKey, item.logId) ?? '-'}
            </Typography>
          </Box>
        );

      case 'changeIndicator':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Change Indicator')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatChangeIndicatorLabel(item.changeIndicator)}
            </Typography>
          </Box>
        );

      case 'entitlementDeviation': {
        let deviationText = '-';

        if (typeof item.entitlementDeviation === 'boolean') {
          deviationText = item.entitlementDeviation ? 'Yes' : 'No';
        }

        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Entitlement Deviation')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {deviationText}
            </Typography>
          </Box>
        );
      }

      case 'tcodeFunctionArea': {
        const { displayText, remaining } = formatMultiShared(
          item.functionNames ?? []
        );

        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Functions')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {displayText}
              {remaining > 0 && (
                <div className='Aag-TimelineItem-additionalCount'>
                  <div>{PLUS}</div>
                  <div>{remaining}</div>
                </div>
              )}
            </Typography>
          </Box>
        );
      }

      case 'shortText':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Details')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {formatDisply(item.shortText, item.details, item.auditClass) ??
                '-'}
            </Typography>
          </Box>
        );

      case 'businessProcessNames': {
        const { displayText, remaining } = formatMultiShared(
          item.businessProcessNames ?? []
        );

        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Business Processes')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {displayText}
              {remaining > 0 && (
                <div className='Aag-TimelineItem-additionalCount'>
                  <div> {PLUS}</div>
                  <div>{remaining}</div>
                </div>
              )}
            </Typography>
          </Box>
        );
      }

      case 'functionNames': {
        const { displayText, remaining } = formatMultiShared(
          item.functionNames ?? []
        );

        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Functions')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {displayText}
              {remaining > 0 && (
                <div className='Aag-TimelineItem-additionalCount'>
                  <div> {PLUS}</div>
                  <div>{remaining}</div>
                </div>
              )}
            </Typography>
          </Box>
        );
      }

      // Removed legacy usagePeriod and usageValue fields (not in new schema)
      case 'usageStartDate':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Usage Start Date')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {item.usageStartDate
                ? formatDateByGlobal(item.usageStartDate, globalDateFormat)
                : '-'}
            </Typography>
          </Box>
        );
      case 'usageEndDate':
        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {l('Usage End Date')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {item.usageEndDate
                ? formatDateByGlobal(item.usageEndDate, globalDateFormat)
                : '-'}
            </Typography>
          </Box>
        );

      default: {
        const prettyLabel = apiId
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (c) => c.toUpperCase());
        const rawVal = (item as unknown as Record<string, unknown>)[apiId];
        let displayText = '-';
        let remainingCount = 0;

        if (Array.isArray(rawVal)) {
          ({ displayText, remaining: remainingCount } =
            formatMultiShared(rawVal));
        } else if (typeof rawVal === 'string') {
          displayText = rawVal.trim().length > 0 ? rawVal : '-';
        } else if (rawVal !== null && rawVal !== undefined) {
          displayText = String(rawVal);
        }

        return (
          <Box className='Aag-TimelineItem-attribute'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='Aag-TimelineItem-attributeLabel'>
              {prettyLabel}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimelineItem-attributeValue'>
              {displayText}
              {remainingCount > 0 && (
                <Typography
                  kind='body3-bold'
                  className='Aag-TimelineItem-additionalCount'>
                  {PLUS}
                  {remainingCount}
                </Typography>
              )}
            </Typography>
          </Box>
        );
      }
    }
  };

  const renderAttributesList = (): React.ReactElement => {
    // If visibleBaseKeys is provided (from column picker), treat them as API ids
    // Otherwise, prefer appliedColumnFilters for this group; fallback to defaults
    let keysToRender: string[] = [];

    if (Array.isArray(visibleBaseKeys) && visibleBaseKeys.length > 0) {
      keysToRender = visibleBaseKeys;
    } else if (
      appliedColumnFilters &&
      Array.isArray(appliedColumnFilters[groupPrefix])
    ) {
      const candidate = appliedColumnFilters[groupPrefix];
      const groupList: string[] = Array.isArray(candidate) ? candidate : [];

      if (groupList.length > 0) {
        keysToRender = groupList.map((full) =>
          full.replace(`${groupPrefix}-`, '')
        );
      }
    } else {
      switch (groupPrefix) {
        case 'SEC_AUDIT_LOG':
          keysToRender = ['auditClass', 'severity', 'clientName', 'tableKey'];
          break;
        case 'CHANGE_LOG':
          keysToRender = ['shortText', 'severity', 'tableKey', 'clientName'];
          break;
        case 'TCODE_LOG':
          keysToRender = [
            'tcodeValue',
            'severity',
            'tcodeFunctionArea',
            'usageStartDate',
            'usageEndDate',
          ];
          break;
        case 'SYSTEM_LOG':
          keysToRender = [
            'auditClass',
            'severity',
            'tcodeDescription',
            'clientName',
          ];
          break;
        default:
          keysToRender = ['auditClass', 'severity', 'clientName', 'tableKey'];
      }
    }

    const limitedKeys = keysToRender.slice(
      0,
      attributeCapacity ?? keysToRender.length
    );

    return (
      <Box className='Aag-TimelineItem-attributes'>
        <div
          className='Aag-TimelineItem-attributeRow'
          ref={attributeRowRef}
          style={{
            // Pass the number of columns to CSS grid via a custom property
            // Fallback to 1 if capacity is null
            ['--cols' as never]: String(attributeCapacity ?? 1),
          }}>
          {limitedKeys.map((k) =>
            shouldShowForApiId(k) ? renderAttributeByApiId(k) : null
          )}
        </div>
      </Box>
    );
  };

  const itemClasses = cx(
    'Aag-TimelineItem',
    {
      'Aag-TimelineItem--last': isLast,
      'Aag-TimelineItem--clickable': !!onClick,
    },
    className
  );

  if (onClick) {
    return (
      <div
        className={cx(itemClasses, {
          'Aag-TimelineItem--selected': isSelected,
        })}>
        {/* Actions Column */}
        {!isReadOnly && (
          <TimelineItemActions
            handleFlagSave={handleFlagSave}
            handleCommentSave={handleCommentSave}
            handleFlagResolve={handleFlagResolve}
            isFlagPopoverOpen={isFlagPopoverOpen}
            setIsFlagPopoverOpen={setIsFlagPopoverOpen}
            flagData={popoverFlagData}
            onCommentClick={handleCommentClick}
            onResolveFlagClick={handleResolveFlagClick}
            onViewActivities={triggerAttributeRowClick}
            commentsCount={item.commentsCount ?? undefined}
            rowLogStatus={effectiveRowStatus}
          />
        )}

        {/* Main Content - Only this section is clickable */}
        <button
          type='button'
          className='Aag-TimelineItem-mainSection Aag-TimelineItem-mainSection--clickable'
          onClick={() => onClick()}>
          {/* Header */}
          <Box className='Aag-TimelineItem-header'>
            <Typography
              lineLimit={1}
              kind='h4'
              className='Aag-TimelineItem-title'>
              {item.rowHeaderName}
            </Typography>
            {item.entitlementDeviation === true && (
              <Tooltip
                text={l('This T-Code was not part of the approved request')}
                placement='top'
                trigger={
                  <Chip
                    kind='outlined'
                    size='xxSmall'
                    color='critical'
                    leftIcon={
                      <Icon kind='AlertCriticalOutline' size='xSmall' />
                    }
                    label={l('Deviate')}
                  />
                }
              />
            )}
            {!isDetailPanelOpen && (
              <Box style={{ marginLeft: 'auto' }}>{statusChip}</Box>
            )}
          </Box>

          {/* Attributes */}
          {renderAttributesList()}
        </button>

        {/* Timeline Bar */}
        <Box className='Aag-TimelineItem-bar'>
          <svg
            className='Aag-TimelineItem-badge'
            xmlns='http://www.w3.org/2000/svg'
            width='12'
            height='12'
            viewBox='0 0 12 12'
            fill='none'
            aria-hidden>
            <circle
              cx='6'
              cy='6'
              r='5'
              fill='var(--color-background-secondary-bold, #254B86)'
              stroke='var(--color-border-secondary-decorative, #F2F4F8)'
              strokeWidth='2'
            />
          </svg>
          {!isLast && <Box className='Aag-TimelineItem-line' />}
        </Box>

        {/* Timestamp */}
        <Box className='Aag-TimelineItem-timestamp'>
          {onTimestampClick ? (
            <button
              type='button'
              className='Aag-TimelineItem-timestampButton'
              onClick={handleTimestampClick}>
              <Typography
                lineLimit={3}
                kind='body3-bold'
                color={CF_PRI_MED}
                className='Aag-TimelineItem-timestampText'>
                {formattedTimestamp}
              </Typography>
            </button>
          ) : (
            <Typography
              lineLimit={3}
              kind='body3'
              color={CF_SEC_MED}
              className='Aag-TimelineItem-timestampText'>
              {formattedTimestamp}
            </Typography>
          )}
        </Box>

        {/* Comment popover is unified with the flag popover anchored on the action button */}
      </div>
    );
  }

  const nonClickableClasses = cx(
    'Aag-TimelineItem',
    {
      'Aag-TimelineItem--last': isLast,
    },
    className
  );

  return (
    <div
      className={cx(nonClickableClasses, {
        'Aag-TimelineItem--selected': isSelected,
      })}>
      {/* Actions Column */}
      {!isReadOnly && (
        <TimelineItemActions
          handleFlagSave={handleFlagSave}
          handleCommentSave={handleCommentSave}
          handleFlagResolve={handleFlagResolve}
          isFlagPopoverOpen={isFlagPopoverOpen}
          setIsFlagPopoverOpen={setIsFlagPopoverOpen}
          flagData={popoverFlagData}
          onCommentClick={handleCommentClick}
          onResolveFlagClick={handleResolveFlagClick}
          onViewActivities={triggerAttributeRowClick}
          commentsCount={item.commentsCount ?? undefined}
          rowLogStatus={effectiveRowStatus}
        />
      )}

      {/* Main Content */}
      <Box className='Aag-TimelineItem-mainSection'>
        {/* Header */}
        <Box className='Aag-TimelineItem-header'>
          <Typography
            lineLimit={1}
            kind='h4'
            className='Aag-TimelineItem-title'>
            {item.rowHeaderName}
          </Typography>
          {item.entitlementDeviation === true && (
            <Tooltip
              text={l('This T-Code was not part of the approved request')}
              placement='top'
              trigger={
                <Chip
                  kind='outlined'
                  size='xxSmall'
                  color='critical'
                  leftIcon={<Icon kind='AlertCriticalOutline' size='xSmall' />}
                  label={l('Deviate')}
                />
              }
            />
          )}
          {!isDetailPanelOpen && (
            <Box style={{ marginLeft: 'auto' }}>{statusChip}</Box>
          )}
        </Box>

        {/* Attributes */}
        {renderAttributesList()}
      </Box>

      {/* Timeline Bar */}
      <Box className='Aag-TimelineItem-bar'>
        <svg
          className='Aag-TimelineItem-badge'
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='12'
          viewBox='0 0 12 12'
          fill='none'
          aria-hidden>
          <circle
            cx='6'
            cy='6'
            r='5'
            fill='var(--color-background-secondary-bold, #254B86)'
            stroke='var(--color-border-secondary-decorative, #F2F4F8)'
            strokeWidth='2'
          />
        </svg>
        {!isLast && <Box className='Aag-TimelineItem-line' />}
      </Box>

      {/* Timestamp */}
      <Box className='Aag-TimelineItem-timestamp'>
        {onTimestampClick ? (
          <button
            type='button'
            className='Aag-TimelineItem-timestampButton'
            onClick={handleTimestampClick}>
            <Typography
              lineLimit={3}
              kind='body3'
              color={CF_PRI_MED}
              className='Aag-TimelineItem-timestampText'>
              {formattedTimestamp}
            </Typography>
          </button>
        ) : (
          <Typography
            lineLimit={3}
            kind='body3'
            color={CF_SEC_MED}
            className='Aag-TimelineItem-timestampText'>
            {formattedTimestamp}
          </Typography>
        )}
      </Box>

      {/* Comment popover is unified with the flag popover anchored on the action button */}
    </div>
  );
}

TimelineItemComponent.defaultProps = {
  isSelected: false,
  isLast: false,
  onClick: undefined,
  onTimestampClick: undefined,
  className: '',
  appliedColumnFilters: undefined,
  visibleBaseKeys: undefined,
  isDetailPanelOpen: false,
};

// Custom comparator to avoid unnecessary re-renders of unchanged rows
const TimelineItem = memo(TimelineItemComponent, (prevProps, nextProps) => {
  // Stable flags
  if (prevProps.isSelected !== nextProps.isSelected) {
    return false;
  }

  if (prevProps.isLast !== nextProps.isLast) {
    return false;
  }

  if (prevProps.className !== nextProps.className) {
    return false;
  }

  if (prevProps.isDetailPanelOpen !== nextProps.isDetailPanelOpen) {
    return false;
  }

  // Compare only the fields that affect visual state
  const p = prevProps.item;
  const n = nextProps.item;

  if (p.uniqueLogId !== n.uniqueLogId) {
    return false;
  }

  if (p.rowLogStatus !== n.rowLogStatus) {
    return false;
  }

  if (p.commentsCount !== n.commentsCount) {
    return false;
  }

  // Latest activity summary shown in popover header/body
  const pAct = p.latestActivity ?? null;
  const nAct = n.latestActivity ?? null;

  if (Boolean(pAct) !== Boolean(nAct)) {
    return false;
  }

  if (pAct && nAct) {
    if (pAct.status !== nAct.status) {
      return false;
    }

    if (pAct.comment !== nAct.comment) {
      return false;
    }

    if (pAct.updatedBy !== nAct.updatedBy) {
      return false;
    }

    if (pAct.updatedAt !== nAct.updatedAt) {
      return false;
    }
  }

  // Re-render when column filters change so visible attributes update
  if (prevProps.appliedColumnFilters !== nextProps.appliedColumnFilters) {
    return false;
  }

  // Ignore function prop identity changes (onClick/onTimestampClick) intentionally
  return true;
});

/**
 * Wrapper component with default props for linter compatibility
 */
function TimelineItemWithDefaults({
  item,
  isSelected = false,
  isLast = false,
  onClick = undefined,
  onTimestampClick = undefined,
  className = '',
  appliedColumnFilters = undefined,
  visibleBaseKeys = undefined,
  isDetailPanelOpen = false,
}: TimelineItemProps) {
  return (
    <TimelineItem
      isSelected={isSelected}
      isLast={isLast}
      onClick={onClick}
      onTimestampClick={onTimestampClick}
      className={className}
      item={item}
      appliedColumnFilters={appliedColumnFilters}
      visibleBaseKeys={visibleBaseKeys}
      isDetailPanelOpen={isDetailPanelOpen}
    />
  );
}

TimelineItemWithDefaults.defaultProps = {
  isSelected: false,
  isLast: false,
  onClick: undefined,
  onTimestampClick: undefined,
  className: '',
  appliedColumnFilters: undefined,
  visibleBaseKeys: undefined,
  isDetailPanelOpen: false,
};

export default TimelineItemWithDefaults;
